import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, IconButton, Chip, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Autocomplete, Grid, Divider, Tooltip,
  CircularProgress, ToggleButtonGroup, ToggleButton, Stack,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  format, startOfWeek, addDays, addWeeks, subWeeks, parseISO,
  startOfMonth, endOfMonth, addMonths, subMonths, isSameMonth, isSameDay,
} from 'date-fns';
import api from '../services/api';

// ─── Constants ────────────────────────────────────────────────────────────────

const START_LOCATIONS = [
  'Job Site',
  'ACS Westbury',
  'ACS IT',
  'ACS Connecticut',
  'Direct from home',
  'Other',
];

const DISCIPLINE_COLORS = {
  AV:       { bg: '#E3F2FD', fg: '#1565C0' },
  IT:       { bg: '#E0F7FA', fg: '#00838F' },
  LIGHTING: { bg: '#F3E5F5', fg: '#7B1FA2' },
  SHADES:   { bg: '#FFF3E0', fg: '#E65100' },
  // Legacy alias for any old 'LT' rows created before the Lighting/Shades split.
  LT:       { bg: '#F3E5F5', fg: '#7B1FA2' },
};

const DISCIPLINE_LABEL = {
  AV: 'AV', IT: 'IT', LIGHTING: 'Lighting', SHADES: 'Shades', LT: 'Lighting/Shades',
};

// Filter chips (order matters for the UI row).
const DISCIPLINE_FILTERS = ['AV', 'IT', 'LIGHTING', 'SHADES'];

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// ─── API helpers ──────────────────────────────────────────────────────────────

const API = '/api/dispatch-template';

// Note: api base URL is '/api' so we strip the leading '/api' from paths.
const PATH = '/dispatch-template';
async function fetchWeek(startISO, endISO) {
  const r = await api.get(PATH, { params: { start: startISO, end: endISO } });
  return r.data;
}
async function fetchTechs() {
  const r = await api.get(`${PATH}/technicians`);
  return r.data.technicians || [];
}
async function searchJobs(q) {
  if (!q || q.length < 2) return [];
  const r = await api.get(`${PATH}/job-search`, { params: { q } });
  return r.data.jobs || [];
}
async function createAssignment(payload) {
  const r = await api.post(PATH, payload);
  return r.data;
}
async function updateAssignment(id, payload, applyToGroup = false) {
  const r = await api.put(`${PATH}/${id}`, payload, { params: applyToGroup ? { apply_to_group: 1 } : {} });
  return r.data;
}
async function deleteAssignment(id) {
  await api.delete(`${PATH}/${id}`);
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function DispatchTemplate() {
  const [layout, setLayout] = useState('week'); // 'week' | 'month'
  const [anchor, setAnchor] = useState(new Date()); // date pointing into the visible period
  const [assignments, setAssignments] = useState([]);
  const [techs, setTechs] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [view, setView] = useState('tech'); // 'tech' | 'job' — job view lands in Phase 2
  const [editing, setEditing] = useState(null); // { mode: 'create'|'edit', card?, prefillDate? }
  const [disciplineFilter, setDisciplineFilter] = useState([]); // [] = show all

  // Compute visible date range based on layout.
  const { rangeStart, rangeEnd, gridDates } = useMemo(() => {
    if (layout === 'month') {
      const monthStart = startOfMonth(anchor);
      const monthEnd = endOfMonth(anchor);
      // Grid runs Mon–Sun weeks covering the whole month.
      const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
      const gridEnd = addDays(startOfWeek(monthEnd, { weekStartsOn: 1 }), 6);
      const days = [];
      for (let d = gridStart; d <= gridEnd; d = addDays(d, 1)) days.push(d);
      return { rangeStart: gridStart, rangeEnd: gridEnd, gridDates: days };
    }
    const ws = startOfWeek(anchor, { weekStartsOn: 1 });
    return {
      rangeStart: ws,
      rangeEnd: addDays(ws, 6),
      gridDates: Array.from({ length: 7 }, (_, i) => addDays(ws, i)),
    };
  }, [anchor, layout]);
  const startISO = format(rangeStart, 'yyyy-MM-dd');
  const endISO = format(rangeEnd, 'yyyy-MM-dd');

  const loadWeek = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await fetchWeek(startISO, endISO);
      setAssignments(data.assignments || []);
      setCanEdit(!!(data.permissions && data.permissions.can_edit));
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [startISO, endISO]);

  useEffect(() => { loadWeek(); }, [loadWeek]);
  useEffect(() => { fetchTechs().then(setTechs).catch(() => {}); }, []);

  // Apply discipline filter (empty = show all).
  const filteredAssignments = useMemo(() => {
    if (!disciplineFilter.length) return assignments;
    const set = new Set(disciplineFilter);
    // 'LIGHTING' or 'SHADES' filter also matches legacy 'LT' rows so nothing hides silently.
    const includeLegacyLT = set.has('LIGHTING') || set.has('SHADES');
    return assignments.filter((a) => {
      const d = a.discipline;
      if (!d) return false;
      if (set.has(d)) return true;
      if (includeLegacyLT && d === 'LT') return true;
      return false;
    });
  }, [assignments, disciplineFilter]);

  // Group cards by date for grid rendering.
  const cardsByDate = useMemo(() => {
    const map = {};
    for (const d of gridDates) map[format(d, 'yyyy-MM-dd')] = [];
    for (const a of filteredAssignments) {
      const key = a.assignment_date ? String(a.assignment_date).slice(0, 10) : null;
      if (key && map[key]) map[key].push(a);
    }
    return map;
  }, [filteredAssignments, gridDates]);

  const handleSaved = () => { setEditing(null); loadWeek(); };
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this assignment? This cannot be undone.')) return;
    try {
      await deleteAssignment(id);
      loadWeek();
    } catch (e) { setError(e.message); }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1600, mx: 'auto' }}>
      {/* PREVIEW BANNER */}
      <Alert
        severity="warning"
        icon={<WarningIcon />}
        sx={{ mb: 2, fontWeight: 500 }}
      >
        🧪 <b>Preview — Dispatch Calendar Template.</b> Play freely, nothing here is live.
        The Google Doc is still the source of truth for real dispatch. Use this to try the new
        workflow and send feedback.
      </Alert>

      {/* HEADER */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Dispatch Calendar Template
        </Typography>
        <Chip label="PREVIEW" size="small" color="warning" />
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup
          value={layout}
          exclusive
          size="small"
          onChange={(_, v) => v && setLayout(v)}
        >
          <ToggleButton value="week">Week</ToggleButton>
          <ToggleButton value="month">Month</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup
          value={view}
          exclusive
          size="small"
          onChange={(_, v) => v && setView(v)}
        >
          <ToggleButton value="tech">Tech-by-Day</ToggleButton>
          <ToggleButton value="job" disabled>Group by Job (Phase 2)</ToggleButton>
        </ToggleButtonGroup>
        <Tooltip title="Email button lands in Phase 3">
          <span>
            <Button startIcon={<EmailIcon />} disabled variant="outlined">
              Email Calendar
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* PERIOD NAV */}
      <Paper sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <IconButton
          onClick={() =>
            setAnchor(layout === 'month' ? subMonths(anchor, 1) : subWeeks(anchor, 1))
          }
        >
          <ChevronLeftIcon />
        </IconButton>
        <Button startIcon={<TodayIcon />} onClick={() => setAnchor(new Date())}>
          {layout === 'month' ? 'This Month' : 'This Week'}
        </Button>
        <IconButton
          onClick={() =>
            setAnchor(layout === 'month' ? addMonths(anchor, 1) : addWeeks(anchor, 1))
          }
        >
          <ChevronRightIcon />
        </IconButton>
        <Typography variant="h6" sx={{ ml: 2 }}>
          {layout === 'month'
            ? format(anchor, 'MMMM yyyy')
            : `${format(rangeStart, 'MMM d')} – ${format(rangeEnd, 'MMM d, yyyy')}`}
        </Typography>
        <Box sx={{ flex: 1 }} />

        {/* Discipline filter chips */}
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mr: 1 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', mr: 0.5 }}>Discipline:</Typography>
          <Chip
            label="All"
            size="small"
            color={disciplineFilter.length === 0 ? 'primary' : 'default'}
            variant={disciplineFilter.length === 0 ? 'filled' : 'outlined'}
            onClick={() => setDisciplineFilter([])}
          />
          {DISCIPLINE_FILTERS.map((d) => {
            const on = disciplineFilter.includes(d);
            const c = DISCIPLINE_COLORS[d];
            return (
              <Chip
                key={d}
                label={DISCIPLINE_LABEL[d]}
                size="small"
                variant={on ? 'filled' : 'outlined'}
                onClick={() => {
                  setDisciplineFilter((prev) =>
                    prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]
                  );
                }}
                sx={on ? { bgcolor: c.bg, color: c.fg, fontWeight: 700, borderColor: c.fg } : { borderColor: c.fg, color: c.fg }}
              />
            );
          })}
        </Stack>

        {canEdit && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setEditing({ mode: 'create' })}
          >
            New Assignment
          </Button>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <CircularProgress size={20} sx={{ ml: 1 }} />}

      {/* PERIOD GRID */}
      {layout === 'week' ? (
        <Grid container spacing={1.5}>
          {gridDates.map((d, i) => {
            const key = format(d, 'yyyy-MM-dd');
            const cards = cardsByDate[key] || [];
            const isToday = isSameDay(d, new Date());
            return (
              <Grid item xs={12} sm={6} md={12/7} key={key}>
                <Paper
                  sx={{
                    p: 1,
                    minHeight: 320,
                    bgcolor: isToday ? '#FFF8E1' : '#FAFAFA',
                    border: isToday ? '2px solid #FFC107' : '1px solid #E0E0E0',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">{DAY_LABELS[i]}</Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {format(d, 'MMM d')}
                      </Typography>
                    </Box>
                    {canEdit && (
                      <IconButton
                        size="small"
                        onClick={() => setEditing({ mode: 'create', prefillDate: key })}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  <Stack spacing={1}>
                    {cards.length === 0 && (
                      <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                        No assignments
                      </Typography>
                    )}
                    {cards.map((c) => (
                      <AssignmentCard
                        key={c.id}
                        card={c}
                        canEdit={canEdit}
                        onEdit={() => setEditing({ mode: 'edit', card: c })}
                        onDelete={() => handleDelete(c.id)}
                      />
                    ))}
                  </Stack>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <MonthGrid
          anchor={anchor}
          gridDates={gridDates}
          cardsByDate={cardsByDate}
          canEdit={canEdit}
          onEdit={(c) => setEditing({ mode: 'edit', card: c })}
          onCreate={(key) => setEditing({ mode: 'create', prefillDate: key })}
          onDelete={handleDelete}
        />
      )}

      {editing && (
        <AssignmentDialog
          mode={editing.mode}
          card={editing.card}
          prefillDate={editing.prefillDate}
          techs={techs}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}
    </Box>
  );
}

// ─── Month Grid ──────────────────────────────────────────────────────────────

function MonthGrid({ anchor, gridDates, cardsByDate, canEdit, onEdit, onCreate, onDelete }) {
  const today = new Date();
  return (
    <Box>
      {/* Day-of-week header row */}
      <Grid container spacing={0.5} sx={{ mb: 0.5 }}>
        {DAY_LABELS.map((d) => (
          <Grid item xs={12/7} key={d}>
            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', fontWeight: 700, color: 'text.secondary' }}>
              {d}
            </Typography>
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={0.5}>
        {gridDates.map((d) => {
          const key = format(d, 'yyyy-MM-dd');
          const cards = cardsByDate[key] || [];
          const inMonth = isSameMonth(d, anchor);
          const isToday = isSameDay(d, today);
          return (
            <Grid item xs={12/7} key={key}>
              <Paper
                variant="outlined"
                sx={{
                  p: 0.75,
                  minHeight: 130,
                  height: '100%',
                  bgcolor: !inMonth ? '#FAFAFA' : (isToday ? '#FFF8E1' : '#FFFFFF'),
                  opacity: inMonth ? 1 : 0.55,
                  border: isToday ? '2px solid #FFC107' : '1px solid #E0E0E0',
                  overflow: 'hidden',
                  display: 'flex', flexDirection: 'column',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: isToday ? '#E65100' : 'text.primary' }}>
                    {format(d, 'd')}
                  </Typography>
                  {canEdit && inMonth && (
                    <IconButton size="small" sx={{ p: 0.25 }} onClick={() => onCreate(key)}>
                      <AddIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                </Box>
                <Stack spacing={0.25} sx={{ flex: 1, overflow: 'hidden' }}>
                  {cards.slice(0, 4).map((c) => (
                    <MonthCardPill key={c.id} card={c} onClick={() => onEdit(c)} canEdit={canEdit} />
                  ))}
                  {cards.length > 4 && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                      +{cards.length - 4} more
                    </Typography>
                  )}
                </Stack>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

function MonthCardPill({ card, onClick, canEdit }) {
  const disc = card.discipline || null;
  const c = disc ? DISCIPLINE_COLORS[disc] : null;
  const label = [card.job_number, card.job_name].filter(Boolean).join(' — ');
  return (
    <Tooltip title={label + (card.start_time ? ` · ${String(card.start_time).slice(0,5)}` : '')}>
      <Box
        onClick={canEdit ? onClick : undefined}
        sx={{
          px: 0.5, py: 0.25, borderRadius: 0.5,
          bgcolor: c ? c.bg : '#EEEEEE',
          color: c ? c.fg : 'text.primary',
          borderLeft: c ? `3px solid ${c.fg}` : '3px solid #BDBDBD',
          fontSize: 10, lineHeight: 1.2,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          cursor: canEdit ? 'pointer' : 'default',
          fontWeight: 600,
        }}
      >
        {card.start_time ? `${String(card.start_time).slice(0,5)} ` : ''}{label || '(no name)'}
      </Box>
    </Tooltip>
  );
}

// ─── Assignment Card (in week grid) ──────────────────────────────────────────

function AssignmentCard({ card, canEdit, onEdit, onDelete }) {
  const disc = card.discipline || null;
  const discColor = disc ? DISCIPLINE_COLORS[disc] : null;
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1,
        cursor: canEdit ? 'pointer' : 'default',
        '&:hover': canEdit ? { boxShadow: 2 } : {},
        borderLeft: disc ? `4px solid ${discColor.fg}` : undefined,
      }}
      onClick={canEdit ? onEdit : undefined}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
        {disc && (
          <Chip
            label={DISCIPLINE_LABEL[disc] || disc}
            size="small"
            sx={{ bgcolor: discColor.bg, color: discColor.fg, fontWeight: 700, height: 18, fontSize: 10 }}
          />
        )}
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {card.job_number || '(no job #)'}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {canEdit && (
          <IconButton
            size="small"
            sx={{ p: 0.25 }}
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
          >
            <DeleteIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2, mb: 0.5 }}>
        {card.job_name || '(no name)'}
      </Typography>
      {card.start_time && (
        <Typography variant="caption" color="text.secondary" display="block">
          🕒 {String(card.start_time).slice(0,5)} · {card.start_location === 'Other' ? card.start_location_other : card.start_location}
        </Typography>
      )}
      {card.pm_name && (
        <Typography variant="caption" color="text.secondary" display="block">
          PM: {card.pm_name}
        </Typography>
      )}
      {card.techs && card.techs.length > 0 && (
        <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
          {card.techs.map((t) => (
            <Chip key={t.id} label={t.tech_name} size="small" sx={{ height: 18, fontSize: 10 }} />
          ))}
        </Box>
      )}
      {card.work_to_complete && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#555' }} noWrap title={card.work_to_complete}>
          {card.work_to_complete}
        </Typography>
      )}
    </Paper>
  );
}

// ─── Create/Edit Dialog ──────────────────────────────────────────────────────

function AssignmentDialog({ mode, card, prefillDate, techs, onClose, onSaved }) {
  const isEdit = mode === 'edit';

  // Multi-day date selection (create mode only). Edit mode = single card.
  const [dates, setDates] = useState(() => {
    if (isEdit && card) return [String(card.assignment_date).slice(0, 10)];
    if (prefillDate) return [prefillDate];
    return [format(new Date(), 'yyyy-MM-dd')];
  });

  // Job selection
  const [jobQuery, setJobQuery] = useState('');
  const [jobOptions, setJobOptions] = useState([]);
  const [jobLoading, setJobLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  // Form fields
  const [form, setForm] = useState(() => {
    if (isEdit && card) {
      return {
        project_id: card.project_id,
        job_number: card.job_number || '',
        job_name: card.job_name || '',
        job_site_address: card.job_site_address || '',
        discipline: card.discipline || '',
        pm_user_id: card.pm_user_id || null,
        pm_name: card.pm_name || '',
        start_location: card.start_location || 'Job Site',
        start_location_other: card.start_location_other || '',
        start_time: card.start_time ? String(card.start_time).slice(0, 5) : '',
        work_to_complete: card.work_to_complete || '',
        onsite_contact: {
          name: card.onsite_contact?.name || '',
          phone: card.onsite_contact?.phone || '',
          email: card.onsite_contact?.email || '',
          role: card.onsite_contact?.role || '',
        },
        notes: card.notes || '',
      };
    }
    return {
      project_id: null, job_number: '', job_name: '', job_site_address: '', discipline: '',
      pm_user_id: null, pm_name: '',
      start_location: 'Job Site', start_location_other: '', start_time: '08:00',
      work_to_complete: '',
      onsite_contact: { name: '', phone: '', email: '', role: '' },
      notes: '',
    };
  });

  // Tech roster: edit mode = single card; create mode = per-date map
  const [techRoster, setTechRoster] = useState(() => {
    if (isEdit && card) return card.techs || [];
    return []; // shared across all dates on create
  });

  const [applyToGroup, setApplyToGroup] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dlgError, setDlgError] = useState(null);

  // Debounced job search
  useEffect(() => {
    if (!jobQuery || jobQuery.length < 2) { setJobOptions([]); return; }
    let cancelled = false;
    setJobLoading(true);
    const t = setTimeout(async () => {
      try {
        const jobs = await searchJobs(jobQuery);
        if (!cancelled) setJobOptions(jobs);
      } catch { /* ignore */ }
      finally { if (!cancelled) setJobLoading(false); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [jobQuery]);

  const applyJob = (job) => {
    setSelectedJob(job);
    if (!job) return;
    setForm((f) => ({
      ...f,
      project_id: job.project_id,
      job_number: job.job_number || '',
      job_name: job.job_name || '',
      job_site_address: job.site_address || f.job_site_address,
      discipline: job.discipline || f.discipline,
      pm_user_id: job.pm_user_id || f.pm_user_id,
      pm_name: job.pm_name || f.pm_name,
      _missing_address_warning: !!job.missing_address,
    }));
  };

  const handleSave = async () => {
    setSaving(true); setDlgError(null);
    try {
      if (isEdit) {
        const payload = {
          ...form,
          start_time: form.start_time || null,
          techs: techRoster.map((t) => ({
            tech_user_id: t.tech_user_id,
            start_time_override: t.start_time_override || null,
            start_location_override: t.start_location_override || null,
            start_location_other_override: t.start_location_other_override || null,
            notes: t.notes || null,
          })),
        };
        await updateAssignment(card.id, payload, applyToGroup);
      } else {
        const techs_by_date = {};
        for (const d of dates) {
          techs_by_date[d] = techRoster.map((t) => ({
            tech_user_id: t.tech_user_id,
            start_time_override: t.start_time_override || null,
            start_location_override: t.start_location_override || null,
            start_location_other_override: t.start_location_other_override || null,
            notes: t.notes || null,
          }));
        }
        await createAssignment({
          dates,
          ...form,
          start_time: form.start_time || null,
          techs_by_date,
        });
      }
      onSaved();
    } catch (e) {
      setDlgError(e?.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const addDate = (d) => {
    if (!d) return;
    setDates((prev) => Array.from(new Set([...prev, d])).sort());
  };
  const removeDate = (d) => setDates((prev) => prev.filter((x) => x !== d));

  const addTech = (u) => {
    if (!u) return;
    if (techRoster.some((t) => t.tech_user_id === u.id)) return;
    setTechRoster((r) => [...r, {
      tech_user_id: u.id, tech_name: u.name, tech_role: u.role,
      start_time_override: '', start_location_override: '', notes: '',
    }]);
  };
  const removeTech = (tid) => setTechRoster((r) => r.filter((t) => t.tech_user_id !== tid));
  const updateTech = (tid, patch) =>
    setTechRoster((r) => r.map((t) => t.tech_user_id === tid ? { ...t, ...patch } : t));

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEdit ? 'Edit Assignment' : 'New Assignment'}
        {isEdit && card?.group_id && (
          <Chip label={`Part of a multi-day group`} size="small" sx={{ ml: 1 }} color="info" />
        )}
      </DialogTitle>
      <DialogContent dividers>
        {dlgError && <Alert severity="error" sx={{ mb: 2 }}>{dlgError}</Alert>}

        {/* DATES */}
        <Section title="Date(s)">
          {isEdit ? (
            <TextField
              type="date"
              size="small"
              value={dates[0]}
              disabled
              helperText="Date is fixed on existing assignments. Create a new one for a different day."
            />
          ) : (
            <Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                {dates.map((d) => (
                  <Chip
                    key={d}
                    label={format(parseISO(d), 'EEE MMM d')}
                    onDelete={dates.length > 1 ? () => removeDate(d) : undefined}
                    color="primary"
                  />
                ))}
              </Box>
              <TextField
                type="date"
                size="small"
                onChange={(e) => addDate(e.target.value)}
                helperText="Add another date to create a multi-day group. Tech roster is per-day."
              />
            </Box>
          )}
        </Section>

        {/* JOB */}
        <Section title="Job">
          <Autocomplete
            options={jobOptions}
            loading={jobLoading}
            getOptionLabel={(o) => o.label || ''}
            filterOptions={(x) => x}
            onInputChange={(_, v) => setJobQuery(v)}
            onChange={(_, v) => applyJob(v)}
            renderOption={(props, o) => (
              <li {...props} key={o.project_id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  {o.discipline && (
                    <Chip
                      label={DISCIPLINE_LABEL[o.discipline] || o.discipline}
                      size="small"
                      sx={{
                        bgcolor: DISCIPLINE_COLORS[o.discipline]?.bg,
                        color: DISCIPLINE_COLORS[o.discipline]?.fg,
                        fontWeight: 700, height: 20,
                      }}
                    />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                      {o.job_number} — {o.job_name}
                    </Typography>
                    {o.missing_address ? (
                      <Typography variant="caption" sx={{ color: '#C62828', fontWeight: 600 }}>
                        ⚠️ No address on file — fix in D-Tools{o.pm_name ? ` · PM: ${o.pm_name}` : ''}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                        {o.site_address}{o.pm_name ? ` · PM: ${o.pm_name}` : ''}
                      </Typography>
                    )}
                  </Box>
                  {o.is_active_wip && (
                    <Chip label="WIP" size="small" color="success" sx={{ height: 18 }} />
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search by job number or name (AV / IT / Lighting all appear together)"
                placeholder="e.g. 26-0088 or Cohen"
                size="small"
              />
            )}
          />
          <Grid container spacing={1} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Job Number" size="small" fullWidth
                value={form.job_number}
                onChange={(e) => setForm({ ...form, job_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                label="Job Name" size="small" fullWidth
                value={form.job_name}
                onChange={(e) => setForm({ ...form, job_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Discipline" size="small" fullWidth select
                value={form.discipline || ''}
                onChange={(e) => setForm({ ...form, discipline: e.target.value })}
              >
                <MenuItem value="">—</MenuItem>
                <MenuItem value="AV">AV</MenuItem>
                <MenuItem value="IT">IT</MenuItem>
                <MenuItem value="LIGHTING">Lighting</MenuItem>
                <MenuItem value="SHADES">Shades</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Job Site Address" size="small" fullWidth
                value={form.job_site_address}
                onChange={(e) => setForm({ ...form, job_site_address: e.target.value })}
                error={!!form._missing_address_warning && !form.job_site_address}
                helperText={form._missing_address_warning && !form.job_site_address
                  ? '⚠️ This job has no address on file. Fix it in D-Tools, or type the address manually here.'
                  : ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Project Manager (any name — not PM-locked)" size="small" fullWidth
                value={form.pm_name}
                onChange={(e) => setForm({ ...form, pm_name: e.target.value, pm_user_id: null })}
              />
            </Grid>
          </Grid>
        </Section>

        {/* DAY-LEVEL FIELDS */}
        <Section title="Start / Work">
          <Grid container spacing={1}>
            <Grid item xs={12} sm={5}>
              <TextField
                label="Start Location" size="small" fullWidth select
                value={form.start_location}
                onChange={(e) => setForm({ ...form, start_location: e.target.value })}
              >
                {START_LOCATIONS.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
              </TextField>
            </Grid>
            {form.start_location === 'Other' && (
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Specify" size="small" fullWidth
                  value={form.start_location_other}
                  onChange={(e) => setForm({ ...form, start_location_other: e.target.value })}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={3}>
              <TextField
                label="Start Time" size="small" fullWidth type="time"
                InputLabelProps={{ shrink: true }}
                value={form.start_time}
                onChange={(e) => setForm({ ...form, start_time: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Work to be completed for the day"
                size="small" fullWidth multiline rows={3}
                value={form.work_to_complete}
                onChange={(e) => setForm({ ...form, work_to_complete: e.target.value })}
              />
            </Grid>
          </Grid>
        </Section>

        {/* ON-SITE CONTACT */}
        <Section title="On-Site Contact">
          <Grid container spacing={1}>
            <Grid item xs={12} sm={4}>
              <TextField label="Name" size="small" fullWidth
                value={form.onsite_contact.name}
                onChange={(e) => setForm({ ...form, onsite_contact: { ...form.onsite_contact, name: e.target.value } })}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField label="Role" size="small" fullWidth placeholder="Client, GC, Housekeeper…"
                value={form.onsite_contact.role}
                onChange={(e) => setForm({ ...form, onsite_contact: { ...form.onsite_contact, role: e.target.value } })}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField label="Phone" size="small" fullWidth
                value={form.onsite_contact.phone}
                onChange={(e) => setForm({ ...form, onsite_contact: { ...form.onsite_contact, phone: e.target.value } })}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField label="Email" size="small" fullWidth
                value={form.onsite_contact.email}
                onChange={(e) => setForm({ ...form, onsite_contact: { ...form.onsite_contact, email: e.target.value } })}
              />
            </Grid>
          </Grid>
        </Section>

        {/* TECHS */}
        <Section title={`Technicians${!isEdit && dates.length > 1 ? ` (applied to all ${dates.length} days — edit per-day after)` : ''}`}>
          <Autocomplete
            options={techs}
            getOptionLabel={(o) => `${o.name}${o.role ? ` (${o.role})` : ''}`}
            onChange={(_, v) => { addTech(v); }}
            renderInput={(params) => (
              <TextField {...params} label="Add technician" size="small" placeholder="Type name…" />
            )}
            value={null}
            blurOnSelect
          />
          <Box sx={{ mt: 1 }}>
            {techRoster.length === 0 && (
              <Typography variant="caption" color="text.disabled">No techs assigned yet.</Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Overrides are optional — leave blank to use the card’s start time and location.
            </Typography>
            {techRoster.map((t) => (
              <Paper key={t.tech_user_id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.tech_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{t.tech_role}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <FieldLabel>Start time</FieldLabel>
                    <TextField
                      size="small" fullWidth type="time"
                      inputProps={{ step: 300 }}
                      value={t.start_time_override || ''}
                      onChange={(e) => updateTech(t.tech_user_id, { start_time_override: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <FieldLabel>Start location</FieldLabel>
                    <TextField
                      size="small" fullWidth select
                      value={t.start_location_override || ''}
                      onChange={(e) => updateTech(t.tech_user_id, { start_location_override: e.target.value })}
                    >
                      <MenuItem value=""><em>Inherit from card</em></MenuItem>
                      {START_LOCATIONS.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={10} sm={3}>
                    <FieldLabel>Notes</FieldLabel>
                    <TextField
                      size="small" fullWidth
                      placeholder="Tech-specific notes"
                      value={t.notes || ''}
                      onChange={(e) => updateTech(t.tech_user_id, { notes: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={2} sm={1} sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
                    <IconButton onClick={() => removeTech(t.tech_user_id)}><DeleteIcon /></IconButton>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Box>
        </Section>

        <Section title="Notes (internal)">
          <TextField
            fullWidth multiline rows={2} size="small"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </Section>

        {isEdit && card?.group_id && (
          <Alert severity="info" sx={{ mt: 1 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={applyToGroup} onChange={(e) => setApplyToGroup(e.target.checked)} />
              Apply job-info + start-location + work + on-site contact changes to <b>all days</b> in this group.
              (Tech rosters stay per-day.)
            </label>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : isEdit ? 'Save' : `Create${dates.length > 1 ? ` (${dates.length} days)` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function FieldLabel({ children }) {
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25, fontWeight: 600 }}>
      {children}
    </Typography>
  );
}

function Section({ title, children }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {title}
      </Typography>
      <Divider sx={{ mb: 1 }} />
      {children}
    </Box>
  );
}
